import React, { useState, useEffect, useCallback } from "react";
import { getAuth } from "firebase/auth";
import { firestore} from '../firebase';
import { doc, getDoc, updateDoc, collection, query, where, getDocs, deleteDoc } from 'firebase/firestore';
import { loadFriends, handleAddFriend, handleAcceptFriend, handleRejectFriend } from '../utils/friendService';
import "../css/Profile.css";
import { showSuccessToast, showErrorToast } from '../utils/toastUtils';
import { rateLimiter } from '../utils/simpleRateLimiter';
import DOMPurify from 'dompurify';
import { useAuth } from '../AuthContext';
import Navigation from './Navigation';
import avatar1 from '../assets/profile-pictures/fe323b7d-e7d4-485b-9e4e-d104af242562.jpg';
import avatar2 from '../assets/profile-pictures/f581a8d3-ec09-437e-ade6-23004b07e35c.jpg';
import avatar3 from '../assets/profile-pictures/Tugeau 2 - Olivia Chin Mueller.jpg';
import avatar4 from '../assets/profile-pictures/Book Reading Pictures _ Freepik.jpg';
import { updateBookRating } from '../utils/firebaseUtils';
import { Link } from 'react-router-dom';

// Validation constants
const MAX_USERNAME_LENGTH = 30;
const MAX_BIO_LENGTH = 200;
const USERNAME_REGEX = /^[a-zA-Z0-9_]{3,30}$/;

// Input validation functions
const validateUsername = (username) => {
    const sanitized = DOMPurify.sanitize(username.trim());
    if (!USERNAME_REGEX.test(sanitized)) {
        throw new Error('Username must be 3-30 characters and can only contain letters, numbers, and underscores');
    }
    return sanitized;
};

const validateBio = (bio) => {
    const sanitized = DOMPurify.sanitize(bio.trim());
    if (sanitized.length > MAX_BIO_LENGTH) {
        throw new Error(`Bio must be less than ${MAX_BIO_LENGTH} characters`);
    }
    return sanitized;
};

const Profile = () => {
    const { user } = useAuth();
    const [profileInfo, setProfileInfo] = useState({
        name: "",
        username: "",
        bio: "",
        profilePicture: "https://ui-avatars.com/api/?name=User&background=random",
    });
    const [friendsList, setFriendsList] = useState([]);
    const [booksRead, setBooksRead] = useState([]);
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState({
        totalBooks: 0,
        readingStreak: 0,
        lastReadDate: null
    });
    const [showEditModal, setShowEditModal] = useState(false);
    const [showRemoveBookModal, setShowRemoveBookModal] = useState(false);
    const [showAddFriendModal, setShowAddFriendModal] = useState(false);
    const [bookToRemove, setBookToRemove] = useState(null);
    const [friendSearchTerm, setFriendSearchTerm] = useState("");
    const [newBio, setNewBio] = useState("");
    const [newUsername, setNewUsername] = useState("");
    const [selectedProfilePicture, setSelectedProfilePicture] = useState(null);
    const [activeTab, setActiveTab] = useState("books");
    const [wantToRead, setWantToRead] = useState([]);
    const auth = getAuth();

    // Load friends list when component mounts
    useEffect(() => {
        const loadFriendsList = async () => {
            if (!user) return;
            try {
                const friends = await loadFriends(user.uid);
                setFriendsList(friends);
            } catch (error) {
                console.error("Error loading friends:", error);
                showErrorToast("Failed to load friends list");
            }
        };

        loadFriendsList();
    }, [user]);

    // Predefined profile pictures
    const profilePictures = [
        {
            id: 'avatar1',
            url: avatar1,
            name: 'Classic Bookworm'
        },
        {
            id: 'avatar2',
            url: avatar2,
            name: 'Modern Reader'
        },
        {
            id: 'avatar3',
            url: avatar3,
            name: 'Fantasy Fan'
        },
        {
            id: 'avatar4',
            url: avatar4,
            name: 'Mystery Lover'
        }
    ];

    // Move this to the top level with other hooks
    const handleRemoveWantToRead = useCallback(async (bookId) => {
        const user = auth.currentUser;
        if (!user) return;

        try {
            // Find and delete the want to read document
            const wantToReadQuery = query(
                collection(firestore, "wantToRead"),
                where("userId", "==", user.uid),
                where("bookId", "==", bookId)
            );
            const snapshot = await getDocs(wantToReadQuery);
            
            if (!snapshot.empty) {
                const docRef = snapshot.docs[0].ref;
                await deleteDoc(docRef);
                
                // Update local state
                setWantToRead(prev => prev.filter(book => book.id !== bookId));
                showSuccessToast("Book removed from want to read list");
            }
        } catch (error) {
            showErrorToast("Failed to remove book from want to read list");
        }
    }, [auth.currentUser]);

    // Helper function to get week number
    const getWeekNumber = useCallback((date) => {
        const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
        const dayNum = d.getUTCDay() || 7;
        d.setUTCDate(d.getUTCDate() + 4 - dayNum);
        const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
        return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
    }, []);

    // Helper function to get weeks in year
    const getWeeksInYear = useCallback((year) => {
        const d = new Date(year, 11, 31);
        const week = getWeekNumber(d);
        return week === 1 ? 53 : week;
    }, [getWeekNumber]);

    const loadBooks = useCallback(async (userId) => {
        try {
            const finishedBooksQuery = query(
                collection(firestore, "FinishedBook"),
                where("User_ID", "==", userId)
            );
            const booksSnapshot = await getDocs(finishedBooksQuery);

            const books = await Promise.all(booksSnapshot.docs.map(async docSnap => {
                const data = docSnap.data();
                return {
                    title: data.Book_Title,
                    id: data.Book_ID,
                    finishedDate: data.Finished_date?.toDate(),
                    rating: data.Rating,
                    coverUrl: data.Cover_URL || "https://ui-avatars.com/api/?name=Book&background=random"
                };
            }));

            return books.sort((a, b) => b.finishedDate - a.finishedDate);
        } catch (error) {
            return [];
        }
    }, []);

    const loadUserProfile = useCallback(async (userId) => {
        try {
            const userRef = doc(firestore, "users", userId);
            const userDoc = await getDoc(userRef);
            return userDoc.exists() ? userDoc.data() : null;
        } catch (error) {
            return null;
        }
    }, []);

    const loadWantToRead = useCallback(async (userId) => {
        try {
            const wantToReadQuery = query(
                collection(firestore, "wantToRead"),
                where("userId", "==", userId)
            );
            const wantToReadSnapshot = await getDocs(wantToReadQuery);
            return wantToReadSnapshot.docs.map(doc => {
                const data = doc.data();
                return {
                    id: data.bookId,
                    title: data.title,
                    author: data.author,
                    coverUrl: data.coverUrl,
                    addedDate: data.addedDate?.toDate()
                };
            }).sort((a, b) => b.addedDate - a.addedDate);
        } catch (error) {
            return [];
        }
    }, []);

    // Load profile data when user changes
    useEffect(() => {
        const loadProfileData = async () => {
            if (!user) {
                setLoading(false);
                return;
            }

            // Check rate limit before any data loading
            if (!rateLimiter.canMakeRequest(user.uid)) {
                setLoading(false);
                showErrorToast("Too many profile refreshes. Please wait a moment before trying again.");
                return;
            }

            try {
                const [profileData, books, wantToReadBooks, friends] = await Promise.all([
                    loadUserProfile(user.uid),
                    loadBooks(user.uid),
                    loadWantToRead(user.uid),
                    loadFriends(user.uid)
                ]);

                if (profileData) {
                    setProfileInfo({
                        name: profileData.name || "",
                        username: profileData.username || "",
                        bio: profileData.bio || "",
                        profilePicture: profileData.profilePicture || "https://ui-avatars.com/api/?name=User&background=random"
                    });
                }

                setBooksRead(books);
                setWantToRead(wantToReadBooks);
                setFriendsList(friends);

                // Calculate stats
                const totalBooks = books.length;
                let readingStreak = 0;
                let lastReadDate = null;

                if (books.length > 0) {
                    // Sort books by finish date (most recent first)
                    const sortedBooks = [...books].sort((a, b) => b.finishedDate - a.finishedDate);
                    lastReadDate = sortedBooks[0].finishedDate;

                    // Build a set of unique (year, week) pairs
                    const weekSet = new Set();
                    sortedBooks.forEach(book => {
                        const date = book.finishedDate;
                        if (date) {
                            const year = date.getFullYear();
                            const week = getWeekNumber(date);
                            weekSet.add(`${year}-W${week}`);
                        }
                    });

                    // Calculate consecutive weekly streak ending with the most recent week
                    let streak = 0;
                    let currentDate = new Date(lastReadDate);
                    while (true) {
                        const year = currentDate.getFullYear();
                        const week = getWeekNumber(currentDate);
                        if (weekSet.has(`${year}-W${week}`)) {
                            streak++;
                            // Move to previous week
                            currentDate.setDate(currentDate.getDate() - 7);
                        } else {
                            break;
                        }
                    }
                    readingStreak = streak;
                }

                setStats({
                    totalBooks,
                    readingStreak,
                    lastReadDate
                });

                setLoading(false);
            } catch (error) {
                console.error("Error loading profile data:", error);
                showErrorToast("Failed to load profile data");
                setLoading(false);
            }
        };

        loadProfileData();
    }, [user, loadUserProfile, loadBooks, loadWantToRead, getWeeksInYear, getWeekNumber]);

    if (loading) {
        return (
            <div className="profile-page">
                <div className="loading-spinner">Loading...</div>
            </div>
        );
    }

    const handleEditProfile = async () => {
        setNewUsername(profileInfo.username);
        setNewBio(profileInfo.bio);
        setShowEditModal(true);
    };

    const handleSaveProfile = async () => {
        try {
            // Check rate limit for profile updates
            if (!rateLimiter.canMakeRequest(auth.currentUser.uid)) {
                showErrorToast("Too many profile updates. Please wait a moment before trying again.");
                return;
            }

            // Validate inputs
            const sanitizedUsername = validateUsername(newUsername);
            const sanitizedBio = validateBio(newBio);

            // Check if username is already taken by another user
            const usersQuery = query(
                collection(firestore, "users"),
                where("username", "==", sanitizedUsername)
            );
            const usersSnapshot = await getDocs(usersQuery);
            
            // If username exists and belongs to a different user
            if (!usersSnapshot.empty && usersSnapshot.docs[0].id !== auth.currentUser.uid) {
                showErrorToast("This username is already taken. Please choose another one.");
                return;
            }

            let profilePictureUrl = selectedProfilePicture || profileInfo.profilePicture;

            const userRef = doc(firestore, "users", auth.currentUser.uid);
            await updateDoc(userRef, {
                bio: sanitizedBio,
                username: sanitizedUsername,
                profilePicture: profilePictureUrl,
                updatedAt: new Date(),
                lastModifiedBy: auth.currentUser.uid
            });

            setProfileInfo(prevInfo => ({ 
                ...prevInfo, 
                bio: sanitizedBio,
                username: sanitizedUsername,
                profilePicture: profilePictureUrl
            }));
            setShowEditModal(false);
            setSelectedProfilePicture(null);
            showSuccessToast("Profile updated successfully!");
        } catch (error) {
            console.error("Error updating profile:", error);
            showErrorToast(error.message || "Failed to update profile. Please try again.");
        }
    };

    const handleCancelEdit = () => {
        setShowEditModal(false);
        setNewBio(profileInfo.bio);
        setNewUsername(profileInfo.username);
        setSelectedProfilePicture(null);
    };

    const handleAddFriendClick = () => {
        setShowAddFriendModal(true);
    };

    const handleConfirmAddFriend = async () => {
        if (!friendSearchTerm?.trim()) {
            setShowAddFriendModal(false);
            return;
        }

        // Check if user is trying to add themselves
        if (friendSearchTerm.trim() === auth.currentUser.email || friendSearchTerm.trim() === profileInfo.username) {
            showErrorToast("Feeling lonely?");
            setShowAddFriendModal(false);
            return;
        }

        try {
            const success = await handleAddFriend(auth.currentUser.uid, auth.currentUser.email, friendSearchTerm);
            if (success) {
                const updatedFriends = await loadFriends(auth.currentUser.uid);
                setFriendsList(updatedFriends);
            }
        } catch (error) {
            console.error("Error adding friend:", error);
            showErrorToast("Failed to add friend. Please try again.");
        } finally {
            setShowAddFriendModal(false);
            setFriendSearchTerm("");
        }
    };

    const handleRemoveBook = async (bookId) => {
        setBookToRemove(bookId);
        setShowRemoveBookModal(true);
    };

    const handleConfirmRemoveBook = async () => {
        try {
            const finishedBookQuery = query(
                collection(firestore, "FinishedBook"),
                where("User_ID", "==", auth.currentUser.uid),
                where("Book_ID", "==", bookToRemove)
            );
            const finishedBookSnapshot = await getDocs(finishedBookQuery);
            
            if (!finishedBookSnapshot.empty) {
                await deleteDoc(doc(firestore, "FinishedBook", finishedBookSnapshot.docs[0].id));
                setBooksRead(prevBooks => prevBooks.filter(book => book.id !== bookToRemove));
                setStats(prev => ({ ...prev, totalBooks: prev.totalBooks - 1 }));
                showSuccessToast("Book removed successfully");
            }
        } catch (error) {
            console.error("Error removing book:", error);
            showErrorToast("Failed to remove book");
        } finally {
            setShowRemoveBookModal(false);
            setBookToRemove(null);
        }
    };

    const handleAcceptFriendClick = async (friendId) => {
        await handleAcceptFriend(friendId, setFriendsList);
    };

    const handleRejectFriendClick = async (friendId) => {
        await handleRejectFriend(friendId, setFriendsList);
    };

    const handleImageError = (e) => {
        e.target.onerror = null; // Prevent infinite loop
        e.target.src = "https://ui-avatars.com/api/?name=User&background=random";
    };

    const handleRatingChange = async (bookId, rating) => {
        try {
            await updateBookRating(auth.currentUser.uid, bookId, rating);
            setBooksRead(prevBooks => 
                prevBooks.map(book => 
                    book.id === bookId ? { ...book, rating } : book
                )
            );
            showSuccessToast("Rating updated successfully!");
        } catch (error) {
            console.error("Error updating rating:", error);
            showErrorToast("Failed to update rating");
        }
    };

    return (
        <div className="profile-page">
            <Navigation />
            <div className="profile-card">
                <img
                    src={profileInfo.profilePicture || "https://ui-avatars.com/api/?name=User&background=random"}
                    alt="Profile"
                    className="profile-pic"
                    onError={handleImageError}
                />
                <h2>{profileInfo.username || profileInfo.name || "User"}</h2>
                <p>{profileInfo.bio || "No bio available"}</p>
                <button onClick={handleEditProfile} className="edit-btn">
                    Edit Profile
                </button>
            </div>

            <div className="profile-tabs">
                <button 
                    className={`tab-button ${activeTab === "books" ? "active" : ""}`}
                    onClick={() => setActiveTab("books")}
                >
                    My Books
                </button>
                <button 
                    className={`tab-button ${activeTab === "wantToRead" ? "active" : ""}`}
                    onClick={() => setActiveTab("wantToRead")}
                >
                    Want to Read
                </button>
                <button 
                    className={`tab-button ${activeTab === "stats" ? "active" : ""}`}
                    onClick={() => setActiveTab("stats")}
                >
                    Stats
                </button>
                <button 
                    className={`tab-button ${activeTab === "friends" ? "active" : ""}`}
                    onClick={() => setActiveTab("friends")}
                >
                    Friends
                </button>
            </div>

            {activeTab === "stats" && (
                <div className="stats-section">
                    <h3>Reading Stats</h3>
                    <div className="stats-detail">
                        <div className="stat-detail-item">
                            <h4>Books Read</h4>
                            <div className="stat-number">{stats.totalBooks}</div>
                            <p>Total books completed</p>
                        </div>
                        <div className="stat-detail-item">
                            <h4>Reading Streak</h4>
                            <div className="stat-number">{stats.readingStreak}</div>
                            <p>Consecutive weeks reading</p>
                        </div>
                        <div className="stat-detail-item">
                            <h4>Last Read</h4>
                            <div className="stat-text">
                                {stats.lastReadDate ? 
                                    new Date(stats.lastReadDate).toLocaleDateString() : 
                                    "Never"}
                            </div>
                            <p>Date of most recent book</p>
                        </div>
                    </div>
                </div>
            )}

            {activeTab === "books" && (
                <div className="books-read-section">
                    <h3>Books Read</h3>
                    <div className="books-grid" id="booksReadGrid">
                        {booksRead.map((book) => (
                            <div key={book.id} className="book-card">
                                <img 
                                    src={book.coverUrl || "https://ui-avatars.com/api/?name=Book&background=random"} 
                                    alt={book.title}
                                    className="book-cover"
                                    onError={handleImageError}
                                />
                                <div className="book-info">
                                    <h4>{book.title}</h4>
                                    <p>Finished: {book.finishedDate?.toLocaleDateString()}</p>
                                    <div className="rating-section">
                                        <p>Your Rating:</p>
                                        <div className="rating-stars">
                                            {[1, 2, 3, 4, 5].map((star) => (
                                                <span
                                                    key={star}
                                                    className={`star ${star <= (book.rating || 0) ? 'filled' : ''}`}
                                                    onClick={() => handleRatingChange(book.id, star)}
                                                >
                                                    ★
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                                <button
                                    className="remove-button"
                                    onClick={() => handleRemoveBook(book.id)}
                                >
                                    Remove
                                </button>
                            </div>
                        ))}
                    </div>
                    <button 
                        className="scroll-indicator scroll-left" 
                        onClick={() => {
                            const grid = document.getElementById('booksReadGrid');
                            grid.scrollBy({ left: -300, behavior: 'smooth' });
                        }}
                    >
                        ←
                    </button>
                    <button 
                        className="scroll-indicator scroll-right" 
                        onClick={() => {
                            const grid = document.getElementById('booksReadGrid');
                            grid.scrollBy({ left: 300, behavior: 'smooth' });
                        }}
                    >
                        →
                    </button>
                </div>
            )}

            {activeTab === "wantToRead" && (
                <div className="want-to-read-section">
                    <h3>Want to Read ({wantToRead.length})</h3>
                    {wantToRead.length === 0 ? (
                        <p>No books in your want to read list yet.</p>
                    ) : (
                        <>
                            <div className="books-grid" id="wantToReadGrid">
                                {wantToRead.map((book) => (
                                    <div key={book.id} className="book-card">
                                        <img 
                                            src={book.coverUrl} 
                                            alt={book.title}
                                            onError={handleImageError}
                                        />
                                        <div className="book-info">
                                            <h4>{book.title}</h4>
                                            <p>{book.author}</p>
                                            <p className="added-date">
                                                Added: {new Date(book.addedDate).toLocaleDateString()}
                                            </p>
                                        </div>
                                        <button
                                            className="remove-button"
                                            onClick={() => handleRemoveWantToRead(book.id)}
                                        >
                                            Remove
                                        </button>
                                    </div>
                                ))}
                            </div>
                            <button 
                                className="scroll-indicator scroll-left" 
                                onClick={() => {
                                    const grid = document.getElementById('wantToReadGrid');
                                    grid.scrollBy({ left: -300, behavior: 'smooth' });
                                }}
                            >
                                ←
                            </button>
                            <button 
                                className="scroll-indicator scroll-right" 
                                onClick={() => {
                                    const grid = document.getElementById('wantToReadGrid');
                                    grid.scrollBy({ left: 300, behavior: 'smooth' });
                                }}
                            >
                                →
                            </button>
                        </>
                    )}
                </div>
            )}

            {activeTab === "friends" && (
                <div className="friends-section">
                    <h3>Friends & Requests</h3>
                    <ul className="friends-list">
                        {friendsList.map((friend) => (
                            <li key={friend.id} className={`friend-item ${friend.status}`}>
                                <div className="friend-info">
                                    <Link 
                                        to={`/friend/${friend.isReceived ? friend.sender : friend.receiver}`}
                                        className="friend-name"
                                        style={{ textDecoration: 'underline' }}
                                    >
                                        {friend.isReceived ? friend.senderUsername : friend.receiverUsername}
                                    </Link>
                                    <span className="friend-status">
                                        {friend.status === "pending" 
                                            ? friend.isReceived ? "Wants to be friends" : "Request sent" 
                                            : "Friend"}
                                    </span>
                                </div>
                                {friend.status === "pending" && friend.isReceived && (
                                    <div className="friend-actions">
                                        <button onClick={() => handleAcceptFriendClick(friend.id)} className="accept-btn">Accept</button>
                                        <button onClick={() => handleRejectFriendClick(friend.id)} className="reject-btn">Reject</button>
                                    </div>
                                )}
                                {friend.status === "accepted" && (
                                    <button onClick={() => handleRejectFriendClick(friend.id)} className="remove-btn">Remove</button>
                                )}
                            </li>
                        ))}
                    </ul>
                    <button onClick={handleAddFriendClick} className="add-btn">Add Friend</button>
                </div>
            )}

            {showEditModal && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <h3>Edit Profile</h3>
                        <div className="form-group">
                            <label>Profile Picture</label>
                            <div className="profile-picture-selector">
                                {profilePictures.map((picture) => (
                                    <div 
                                        key={picture.id}
                                        className={`profile-picture-option ${selectedProfilePicture === picture.url ? 'selected' : ''}`}
                                        onClick={() => setSelectedProfilePicture(picture.url)}
                                    >
                                        <img 
                                            src={picture.url} 
                                            alt={picture.name}
                                            className="profile-picture-preview"
                                            onError={handleImageError}
                                        />
                                        <span className="profile-picture-name">{picture.name}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="form-group">
                            <label>Username (3-30 characters, letters, numbers, and underscores only)</label>
                            <input
                                type="text"
                                id="username"
                                value={newUsername}
                                onChange={(e) => setNewUsername(e.target.value)}
                                placeholder="Enter your username"
                                maxLength={MAX_USERNAME_LENGTH}
                                pattern="^[a-zA-Z0-9_]{3,30}$"
                            />
                            <small className="input-requirements">
                                {newUsername.length}/{MAX_USERNAME_LENGTH} characters
                            </small>
                        </div>
                        <div className="form-group">
                            <label>Bio</label>
                            <textarea
                                id="bio"
                                value={newBio}
                                onChange={(e) => setNewBio(e.target.value)}
                                placeholder="Write something about yourself..."
                                rows="4"
                                maxLength={MAX_BIO_LENGTH}
                            />
                            <small className="input-requirements">
                                {newBio.length}/{MAX_BIO_LENGTH} characters
                            </small>
                        </div>
                        <div className="modal-actions">
                            <button onClick={handleSaveProfile}>SAVE</button>
                            <button onClick={handleCancelEdit} className="cancel-btn">CANCEL</button>
                        </div>
                    </div>
                </div>
            )}

            {showRemoveBookModal && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <h3>Remove Book</h3>
                        <p>Are you sure you want to remove this book from your list?</p>
                        <div className="modal-actions">
                            <button onClick={handleConfirmRemoveBook}>YES</button>
                            <button onClick={() => setShowRemoveBookModal(false)} className="cancel-btn">NO</button>
                        </div>
                    </div>
                </div>
            )}

            {showAddFriendModal && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <h3>Add Friend</h3>
                        <div className="form-group">
                            <label>Enter your friend's email or username:</label>
                            <input
                                type="text"
                                value={friendSearchTerm}
                                onChange={(e) => setFriendSearchTerm(e.target.value)}
                                placeholder="Email or username"
                            />
                        </div>
                        <div className="modal-actions">
                            <button onClick={handleConfirmAddFriend}>ADD</button>
                            <button onClick={() => setShowAddFriendModal(false)} className="cancel-btn">CANCEL</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Profile;
